/**
 * Requisitions: the start of the purchase-to-pay journey.
 *
 * ═══════════════════════════════════════════════════════════════════
 * THE BUDGET CHECK IS REAL, AND IT RUNS BEFORE THE REQUEST IS SUBMITTED.
 *
 * When a requisition is submitted the server checks the budget for each coding
 * combination — the annual budget, less what has been spent, less what is
 * already committed — and decides. What it decides depends on the policy that
 * applies to that cost centre: warn and allow, or block outright. A block can
 * only be passed with an explicit override by somebody holding the override
 * permission, and the override is recorded with its reason.
 *
 * This screen shows the check's own words. It does not summarise them, and it
 * does not let the user proceed by ignoring them.
 * ═══════════════════════════════════════════════════════════════════
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTerms, lower } from '../lib/tenant';
import {
  date,
  dateTime,
  money,
  number,
  previewLineTotal,
  statusLabel,
  statusTone,
  todayIso,
  addDaysIso,
  type BadgeTone,
} from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  DefinitionList,
  EmptyState,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  Select,
  TextArea,
  TextInput,
} from '../components/ui';

interface RequisitionRow {
  id: string;
  requisition_no: string;
  title: string;
  status: string;
  gross_total_minor: number | string;
  supplier_name: string | null;
  proposed_supplier_name: string | null;
  requested_by_name: string | null;
  cost_centre_code: string | null;
  cost_centre_name: string | null;
  created_at: string;
  date_required: string | null;
  budget_check_outcome: string | null;
  order_no: string | null;
  order_id: string | null;
  approval_status: string | null;
  current_stage_name: string | null;
  current_approver_name: string | null;
}

/*
 * The stored status vocabulary, exactly as the database CHECK constraint on
 * purchase_requisition.status permits it. The label is ours; the value is the
 * server's, and it is sent to the API untranslated — a filter that invents its
 * own word silently returns nothing.
 */
const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Awaiting approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'cancelled', label: 'Cancelled' },
];

/**
 * The budget check has its own vocabulary, separate from a document status.
 *
 * `statusTone` maps document statuses, and a passed budget check is not one of
 * them — routed through it, "Pass" comes out the same neutral grey as "Draft".
 * The whole point of the column is to say at a glance that the control ran and
 * was satisfied, so the outcome gets its own tone and its own plain-English
 * wording here.
 */
const BUDGET_OUTCOME_TONE: Record<string, BadgeTone> = {
  pass: 'ok',
  warn: 'warn',
  block: 'error',
  overridden: 'warn',
  not_checked: 'neutral',
};

const BUDGET_OUTCOME_LABEL: Record<string, string> = {
  pass: 'Within budget',
  warn: 'Over budget — allowed',
  block: 'Blocked',
  overridden: 'Overridden',
  not_checked: 'Not yet run',
};

function budgetTone(outcome: string | null | undefined): BadgeTone {
  return outcome ? BUDGET_OUTCOME_TONE[outcome] ?? 'neutral' : 'neutral';
}

function budgetLabel(outcome: string | null | undefined): string {
  return outcome ? BUDGET_OUTCOME_LABEL[outcome] ?? statusLabel(outcome) : 'Not yet run';
}

export function RequisitionsPage() {
  const t = useTerms();
  const { can } = useSession();
  const [status, setStatus] = useState('');

  const list = useAsync(
    () => api.get<{ requisitions: RequisitionRow[] }>(`/procurement/requisitions${status ? `?status=${status}` : ''}`),
    [status],
  );

  return (
    <>
      <PageHeader
        title={t.requisitionPlural}
        subtitle={`A request to buy something. It reserves budget when it is submitted and becomes a ${lower(
          t.purchaseOrder,
        )} once approved.`}
        actions={
          can('requisition.create') ? (
            <Link className="btn btn--primary" to="/requisitions/new">
              Raise a {lower(t.requisition)}
            </Link>
          ) : null
        }
      />

      <Card
        flush
        actions={
          <label className="row" style={{ gap: '0.4rem' }}>
            <span className="smaller muted">Status</span>
            <select value={status} onChange={(e) => setStatus(e.currentTarget.value)}>
              {STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        }
        title={t.requisitionPlural}
      >
        <AsyncPanel state={list}>
          {(data) => (
            <DataTable
              caption={`${t.requisitionPlural} you may see`}
              rows={data.requisitions}
              rowKey={(r) => r.id}
              pageSize={25}
              emptyMessage={`No ${lower(t.requisitionPlural)} match this filter.`}
              columns={[
                {
                  key: 'no',
                  header: 'Number',
                  width: '10rem',
                  sortValue: (r) => r.requisition_no,
                  // A document reference broken across two lines reads as a
                  // layout nobody looked at, so the number keeps its own line
                  // and the table scrolls instead.
                  render: (r) => (
                    <Link to={`/requisitions/${r.id}`}>
                      <span className="mono" style={{ whiteSpace: 'nowrap' }}>
                        {r.requisition_no}
                      </span>
                    </Link>
                  ),
                },
                {
                  key: 'title',
                  header: 'What is being bought',
                  sortValue: (r) => r.title,
                  render: (r) => (
                    <>
                      <div className="truncate">{r.title}</div>
                      <div className="smaller muted">
                        {r.supplier_name ?? r.proposed_supplier_name ?? `No ${lower(t.supplier)} named`}
                        {r.cost_centre_code ? ` · ${r.cost_centre_code}` : ''}
                      </div>
                    </>
                  ),
                },
                {
                  key: 'by',
                  header: 'Raised by',
                  sortValue: (r) => r.requested_by_name ?? '',
                  render: (r) => <span className="smaller">{r.requested_by_name ?? '—'}</span>,
                },
                {
                  key: 'value',
                  header: 'Value',
                  numeric: true,
                  sortValue: (r) => Number(r.gross_total_minor),
                  render: (r) => <Money minor={Number(r.gross_total_minor)} />,
                },
                {
                  key: 'status',
                  header: 'Status',
                  sortValue: (r) => statusLabel(r.status),
                  render: (r) => (
                    <>
                      <Badge tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
                      {r.current_stage_name && r.status === 'pending_approval' ? (
                        <div className="smaller muted">
                          With {r.current_approver_name ?? r.current_stage_name}
                        </div>
                      ) : null}
                      {r.order_no ? (
                        <div className="smaller">
                          <Link to={`/orders/${r.order_id}`}>
                            <span className="mono">{r.order_no}</span>
                          </Link>
                        </div>
                      ) : null}
                    </>
                  ),
                },
                {
                  key: 'budget',
                  header: 'Budget check',
                  sortValue: (r) => budgetLabel(r.budget_check_outcome),
                  render: (r) =>
                    r.budget_check_outcome ? (
                      <Badge tone={budgetTone(r.budget_check_outcome)}>{budgetLabel(r.budget_check_outcome)}</Badge>
                    ) : (
                      <span className="muted smaller">Not yet run</span>
                    ),
                },
                {
                  key: 'raised',
                  header: 'Raised',
                  sortValue: (r) => r.created_at,
                  render: (r) => <span className="smaller">{date(r.created_at)}</span>,
                },
              ]}
              /*
               * The total is of every row the status filter returned, not of the
               * page on screen — which is what anybody asking "how much is in
               * this list?" means.
               */
              footer={
                <tr className="is-total">
                  <td colSpan={3}>Total of {number(data.requisitions.length)} shown</td>
                  <td className="num">
                    {money(data.requisitions.reduce((sum, r) => sum + Number(r.gross_total_minor), 0))}
                  </td>
                  <td colSpan={3} />
                </tr>
              }
            />
          )}
        </AsyncPanel>
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * New requisition
 * ------------------------------------------------------------------ */

interface LineDraft {
  description: string;
  quantity: string;
  unitPrice: string;
  accountId: string;
  costCentreId: string;
  projectId: string;
  vatCode: string;
  isCapital: boolean;
}

const EMPTY_LINE: LineDraft = {
  description: '',
  quantity: '1',
  unitPrice: '',
  accountId: '',
  costCentreId: '',
  projectId: '',
  vatCode: 'S20',
  isCapital: false,
};

interface LineGaps {
  description?: string;
  quantity?: string;
  unitPrice?: string;
  accountId?: string;
  costCentreId?: string;
}

interface FormGaps {
  title?: string;
  supplier?: string;
  costCentre?: string;
  lines: LineGaps[];
  count: number;
}

export function RequisitionNewPage() {
  const t = useTerms();
  const navigate = useNavigate();

  const refs = useAsync(
    () =>
      Promise.all([
        api.get<{ accounts: Array<{ id: string; code: string; name: string; account_type: string; is_capital: boolean; default_vat_code: string | null }> }>(
          '/reference/accounts',
        ),
        api.get<{ costCentres: Array<{ id: string; code: string; name: string; level_type: string }>; isScoped: boolean }>(
          '/reference/cost-centres',
        ),
        api.get<{ suppliers: Array<{ id: string; code: string; name: string; status: string }> }>('/reference/suppliers'),
        api.get<{ projects: Array<{ id: string; code: string; name: string }> }>('/reference/projects'),
        api.get<{ vatCodes: Array<{ code: string; name: string; rate_percent: string }> }>('/reference/vat-codes'),
      ]).then(([accounts, costCentres, suppliers, projects, vatCodes]) => ({
        accounts: accounts.accounts,
        costCentres: costCentres.costCentres,
        suppliers: suppliers.suppliers,
        projects: projects.projects,
        vatCodes: vatCodes.vatCodes,
      })),
    [],
  );

  const [title, setTitle] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [proposedSupplierName, setProposedSupplierName] = useState('');
  const [justification, setJustification] = useState('');
  const [dateRequired, setDateRequired] = useState(addDaysIso(todayIso(), 14));
  const [headerCostCentre, setHeaderCostCentre] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([{ ...EMPTY_LINE }]);
  const [budgetOutcome, setBudgetOutcome] = useState<BudgetCheckResult | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [createdNo, setCreatedNo] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<SubmitResult | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelledNo, setCancelledNo] = useState<string | null>(null);
  const [showGaps, setShowGaps] = useState(false);

  // Once the draft exists the server holds the figures the budget check ruled
  // on, and there is no amend endpoint: editing the boxes afterwards would
  // change nothing that is sent. So the boxes stop accepting edits and the only
  // two offers left — submit again, or cancel the draft — are ones the server
  // can honour.
  const locked = createdId !== null;

  const create = useMutation(async () =>
    api.post<{ id: string; requisitionNo: string }>('/procurement/requisitions', {
      title: title.trim(),
      supplierId: supplierId || null,
      proposedSupplierName: supplierId ? null : proposedSupplierName.trim() || null,
      justification: justification.trim() || null,
      dateRequired,
      costCentreId: headerCostCentre || null,
      lines: lines.map((l) => ({
        description: l.description.trim(),
        quantity: l.quantity.trim(),
        unitPrice: l.unitPrice.trim(),
        accountId: l.accountId,
        costCentreId: l.costCentreId || headerCostCentre,
        projectId: l.projectId || null,
        vatCode: l.vatCode || null,
        isCapital: l.isCapital,
      })),
    }),
  );

  const cancelDraft = useMutation(async (requisitionId: string, reason: string) =>
    api.post<{ ok: boolean }>(`/procurement/requisitions/${requisitionId}/cancel`, { reason: reason.trim() }),
  );

  const netTotal = useMemo(
    () => lines.reduce((sum, l) => sum + (previewLineTotal(l.quantity, l.unitPrice) ?? 0), 0),
    [lines],
  );

  /*
   * What is still missing, said the way the person filling the form would say
   * it. The server validates all of this again and is the authority — this
   * check exists because a schema rejection comes back as an internal error
   * reference rather than a sentence, and somebody who mistypes one box should
   * be told which box, not told to telephone a service desk.
   */
  const gaps = useMemo<FormGaps>(() => {
    const lineGaps = lines.map<LineGaps>((l) => {
      const g: LineGaps = {};
      if (!l.description.trim()) g.description = 'Say what is being bought.';
      const quantity = Number(l.quantity);
      if (!l.quantity.trim() || !Number.isFinite(quantity) || quantity <= 0) {
        g.quantity = 'Enter a quantity greater than zero.';
      }
      const price = Number(l.unitPrice);
      if (!l.unitPrice.trim() || !Number.isFinite(price) || price < 0) {
        g.unitPrice = 'Enter the price of one unit, in pounds.';
      }
      if (!l.accountId) g.accountId = 'Choose the nominal code this line is charged to.';
      if (!l.costCentreId && !headerCostCentre) g.costCentreId = `Choose the ${lower(t.costCentre)} whose budget pays.`;
      return g;
    });

    const result: FormGaps = { lines: lineGaps, count: 0 };
    if (title.trim().length < 3) result.title = 'Give the request a title of at least three characters.';
    if (!supplierId && !proposedSupplierName.trim()) {
      result.supplier = `Choose a ${lower(t.supplier)}, or name the one you propose.`;
    }
    if (!headerCostCentre) result.costCentre = `Choose the ${lower(t.costCentre)} being charged.`;
    result.count =
      (result.title ? 1 : 0) +
      (result.supplier ? 1 : 0) +
      (result.costCentre ? 1 : 0) +
      lineGaps.reduce((n, g) => n + Object.keys(g).length, 0);
    return result;
  }, [title, supplierId, proposedSupplierName, headerCostCentre, lines, t]);

  const setLine = (index: number, patch: Partial<LineDraft>) =>
    setLines((current) => current.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const onCreateAndSubmit = async () => {
    if (gaps.count > 0) {
      setShowGaps(true);
      return;
    }
    setShowGaps(false);
    setCancelledNo(null);

    let requisitionId = createdId;
    if (!requisitionId) {
      const created = await create.run();
      if (!created) return;
      requisitionId = created.id;
      setCreatedId(created.id);
      setCreatedNo(created.requisitionNo);
    }

    // The budget check runs on the server as part of submission. If it blocks,
    // the server refuses and says why — we show its own words and offer the
    // override only to somebody who holds the permission.
    try {
      const result = await api.post<SubmitResult>(`/procurement/requisitions/${requisitionId}/submit`, {
        overrideReason: overrideReason.trim() || null,
      });

      // A warning is not a pass. The check allowed the request through but had
      // something to say about it, and the person who caused the overspend is
      // the one who should read it — so the page stays put and repeats it,
      // rather than replacing it with the detail screen.
      if (result.budgetCheck?.outcome === 'warn') {
        setSubmitted(result);
        setBudgetOutcome(result.budgetCheck);
        return;
      }
      navigate(`/requisitions/${requisitionId}`);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'BUDGET_EXCEEDED') {
        const check = await api
          .get<BudgetCheckResult>(`/procurement/requisitions/${requisitionId}/budget-check`)
          .catch(() => null);
        // The refusal itself already carries the blocked lines, so the panel
        // still fills in if the follow-up read fails.
        setBudgetOutcome(check ?? blockedLinesFrom(e));
      }
      throw e;
    }
  };

  const attempt = useMutation(onCreateAndSubmit);

  const onCancelDraft = async () => {
    if (!createdId) return;
    const done = await cancelDraft.run(createdId, cancelReason);
    if (!done) return;
    setCancelledNo(createdNo);
    setCreatedId(null);
    setCreatedNo(null);
    setBudgetOutcome(null);
    setOverrideReason('');
    setCancelReason('');
    setCancelling(false);
    attempt.clearError();
  };

  return (
    <>
      <PageHeader
        title={`Raise a ${lower(t.requisition)}`}
        subtitle="Say what you need, what it costs and which budget pays for it. The budget is checked and reserved when you submit."
        actions={
          <Link className="btn" to="/requisitions">
            Cancel
          </Link>
        }
      />

      <AsyncPanel state={refs}>
        {(data) => {
          const expenditureAccounts = data.accounts.filter(
            (a) => a.account_type === 'expenditure' || a.is_capital,
          );
          const budgetCostCentres = data.costCentres.filter((c) => c.level_type === 'cost_centre' || c.level_type === 'department');

          return (
            <div className="stack">
              {cancelledNo ? (
                <Alert tone="ok" title={`${cancelledNo} was cancelled`}>
                  <p>
                    The draft and the budget it was holding have been released. The details below can be changed and
                    submitted again as a new {lower(t.requisition)}.
                  </p>
                </Alert>
              ) : null}

              {locked && !submitted ? (
                <Alert tone="info" title={`Draft ${createdNo ?? ''} has been saved`}>
                  <p>
                    The figures below are the ones the budget check ruled on, so they can no longer be changed here.
                    Submit the draft again, or cancel it and start over.
                  </p>
                  {createdId ? (
                    <p className="smaller">
                      <Link to={`/requisitions/${createdId}`}>Open the draft {lower(t.requisition)}</Link>
                    </p>
                  ) : null}
                </Alert>
              ) : null}

              <Card title="What you need">
                <div className="grid grid--2">
                  <TextInput
                    label="Short title"
                    hint={`Your ${lower(t.budgetHolder)} sees this first — make it recognisable.`}
                    value={title}
                    onChange={(e) => setTitle(e.currentTarget.value)}
                    error={showGaps ? gaps.title ?? null : null}
                    disabled={locked}
                    required
                  />
                  <Select
                    label={t.supplier}
                    hint={`Preferred ${lower(t.supplierPlural)} have agreed pricing.`}
                    value={supplierId}
                    onChange={(e) => setSupplierId(e.currentTarget.value)}
                    disabled={locked}
                  >
                    <option value="">Not on file — name the proposed {lower(t.supplier)} below</option>
                    {data.suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                  {/*
                    The server will not accept a request with neither a chosen
                    supplier nor a proposed name, so the box appears the moment
                    the choice is left open rather than after the refusal.
                  */}
                  {!supplierId ? (
                    <TextInput
                      label={`Proposed ${lower(t.supplier)}`}
                      hint={`Recorded on the request so ${lower(
                        t.financeTeam,
                      )} can set the ${lower(t.supplier)} up before the order is placed.`}
                      value={proposedSupplierName}
                      onChange={(e) => setProposedSupplierName(e.currentTarget.value)}
                      error={showGaps ? gaps.supplier ?? null : null}
                      disabled={locked}
                      required
                    />
                  ) : null}
                  <Select
                    label={`${t.costCentre} being charged`}
                    value={headerCostCentre}
                    onChange={(e) => setHeaderCostCentre(e.currentTarget.value)}
                    error={showGaps ? gaps.costCentre ?? null : null}
                    disabled={locked}
                    required
                  >
                    <option value="">Choose…</option>
                    {budgetCostCentres.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.code} — {c.name}
                      </option>
                    ))}
                  </Select>
                  <TextInput
                    label="Date required"
                    type="date"
                    value={dateRequired}
                    onChange={(e) => setDateRequired(e.currentTarget.value)}
                    disabled={locked}
                  />
                </div>
                <TextArea
                  label="Why is it needed?"
                  hint="One or two sentences. This is what an approver reads before deciding."
                  value={justification}
                  onChange={(e) => setJustification(e.currentTarget.value)}
                  disabled={locked}
                />
              </Card>

              <Card
                title="Lines"
                subtitle={`Every line needs a nominal code (what it is) and a ${lower(
                  t.costCentre,
                )} (whose budget pays).`}
                actions={
                  !locked ? (
                    <button type="button" className="btn btn--sm" onClick={() => setLines((c) => [...c, { ...EMPTY_LINE }])}>
                      Add a line
                    </button>
                  ) : null
                }
              >
                <div className="stack">
                  {lines.map((line, index) => {
                    const lineNet = previewLineTotal(line.quantity, line.unitPrice);
                    const lineGaps = showGaps ? gaps.lines[index] : undefined;
                    return (
                      <fieldset key={index} className="line-editor" disabled={locked}>
                        <legend className="visually-hidden">Line {index + 1}</legend>
                        <div className="grid grid--3">
                          <TextInput
                            label="Description"
                            value={line.description}
                            onChange={(e) => setLine(index, { description: e.currentTarget.value })}
                            error={lineGaps?.description ?? null}
                            required
                          />
                          <TextInput
                            label="Quantity"
                            className="num-input"
                            inputMode="decimal"
                            value={line.quantity}
                            onChange={(e) => setLine(index, { quantity: e.currentTarget.value })}
                            error={lineGaps?.quantity ?? null}
                            required
                          />
                          <TextInput
                            label="Unit price (excluding VAT)"
                            className="num-input"
                            inputMode="decimal"
                            value={line.unitPrice}
                            onChange={(e) => setLine(index, { unitPrice: e.currentTarget.value })}
                            error={lineGaps?.unitPrice ?? null}
                            required
                          />
                          <Select
                            label="Nominal code"
                            value={line.accountId}
                            onChange={(e) => {
                              const account = expenditureAccounts.find((a) => a.id === e.currentTarget.value);
                              setLine(index, {
                                accountId: e.currentTarget.value,
                                isCapital: account?.is_capital ?? false,
                                vatCode: account?.default_vat_code ?? line.vatCode,
                              });
                            }}
                            error={lineGaps?.accountId ?? null}
                            required
                          >
                            <option value="">Choose…</option>
                            {expenditureAccounts.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.code} — {a.name}
                              </option>
                            ))}
                          </Select>
                          <Select
                            label={t.costCentre}
                            value={line.costCentreId || headerCostCentre}
                            onChange={(e) => setLine(index, { costCentreId: e.currentTarget.value })}
                            error={lineGaps?.costCentreId ?? null}
                            required
                          >
                            <option value="">Choose…</option>
                            {budgetCostCentres.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.code} — {c.name}
                              </option>
                            ))}
                          </Select>
                          <Select
                            label="VAT"
                            value={line.vatCode}
                            onChange={(e) => setLine(index, { vatCode: e.currentTarget.value })}
                          >
                            {data.vatCodes.map((v) => (
                              <option key={v.code} value={v.code}>
                                {v.code} — {v.name}
                              </option>
                            ))}
                          </Select>
                          {data.projects.length > 0 ? (
                            <Select
                              label={`${t.project} (optional)`}
                              value={line.projectId}
                              onChange={(e) => setLine(index, { projectId: e.currentTarget.value })}
                            >
                              <option value="">None</option>
                              {data.projects.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.code} — {p.name}
                                </option>
                              ))}
                            </Select>
                          ) : null}
                        </div>
                        <div className="row row--between">
                          <span className="smaller muted">
                            {line.isCapital ? <Badge tone="info">Capital — will be tagged as an asset</Badge> : null}
                          </span>
                          <span className="row" style={{ gap: '1rem' }}>
                            <span className="smaller muted">
                              Line net <Money minor={lineNet} />
                            </span>
                            {lines.length > 1 && !locked ? (
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => setLines((c) => c.filter((_, i) => i !== index))}
                              >
                                Remove line
                              </button>
                            ) : null}
                          </span>
                        </div>
                      </fieldset>
                    );
                  })}
                </div>
              </Card>

              <Card>
                <div className="row row--between">
                  <div>
                    <p className="stat__label">Net total</p>
                    <p className="stat__value">
                      <Money minor={netTotal} />
                    </p>
                    <p className="smaller muted">VAT is calculated by the server from the codes on each line.</p>
                  </div>
                  {/*
                    Once the request is submitted neither of these can do
                    anything the server would accept, so neither is drawn: the
                    only way on from here is the link to the record itself.
                  */}
                  <span className="row" style={{ gap: '0.5rem' }}>
                    {locked && !submitted ? (
                      <button
                        type="button"
                        className="btn"
                        disabled={attempt.busy || cancelDraft.busy}
                        onClick={() => setCancelling(true)}
                      >
                        Cancel this draft
                      </button>
                    ) : null}
                    {!submitted ? (
                      <button
                        type="button"
                        className="btn btn--primary"
                        disabled={attempt.busy || create.busy}
                        onClick={() => void attempt.run()}
                      >
                        {attempt.busy
                          ? 'Checking budget and submitting…'
                          : locked
                            ? 'Submit this draft again'
                            : 'Check budget and submit for approval'}
                      </button>
                    ) : null}
                  </span>
                </div>

                {showGaps && gaps.count > 0 ? (
                  <div style={{ marginTop: '1rem' }}>
                    <Alert tone="warn" title="Some details are still needed">
                      <p>
                        {gaps.count === 1
                          ? 'One field above is marked. Fill it in and submit again.'
                          : `${number(gaps.count)} fields above are marked. Fill them in and submit again.`}
                      </p>
                    </Alert>
                  </div>
                ) : null}

                <ErrorNotice error={create.error} />
                {attempt.error && attempt.error.code !== 'BUDGET_EXCEEDED' ? <ErrorNotice error={attempt.error} /> : null}

                {submitted ? (
                  <div style={{ marginTop: '1rem' }}>
                    <Alert tone="warn" title={`${submitted.requisitionNo} was submitted, over budget`}>
                      <p>{submitted.budgetCheck?.message}</p>
                      <p className="smaller">
                        {submitted.approval?.firstApproverName
                          ? `It is now with ${submitted.approval.firstApproverName} for approval.`
                          : 'It is now in the approval queue.'}
                      </p>
                      {budgetOutcome ? <BudgetCheckDetail result={budgetOutcome} /> : null}
                      {createdId ? (
                        <p style={{ marginTop: '1rem' }}>
                          <Link className="btn btn--primary" to={`/requisitions/${createdId}`}>
                            Open {submitted.requisitionNo}
                          </Link>
                        </p>
                      ) : null}
                    </Alert>
                  </div>
                ) : null}

                {attempt.error?.code === 'BUDGET_EXCEEDED' ? (
                  <div style={{ marginTop: '1rem' }}>
                    <Alert tone="error" title="The budget check refused this request">
                      <p>{attempt.error.message}</p>
                      {budgetOutcome ? <BudgetCheckDetail result={budgetOutcome} /> : null}
                      {budgetOutcome?.canOverride ? (
                        <div style={{ marginTop: '1rem' }}>
                          <TextArea
                            label="Override reason"
                            hint={`You hold the budget override permission. The reason is recorded permanently against the ${lower(
                              t.requisition,
                            )} and appears in the audit trail.`}
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.currentTarget.value)}
                            required
                          />
                          <button
                            type="button"
                            className="btn btn--danger"
                            disabled={overrideReason.trim().length < 10 || attempt.busy}
                            onClick={() => void attempt.run()}
                          >
                            Override and submit
                          </button>
                          <p className="smaller muted" style={{ marginTop: '0.4rem' }}>
                            At least ten characters — the reason has to stand up to being read back by an auditor.
                          </p>
                        </div>
                      ) : (
                        <p className="smaller">
                          You do not hold the budget override permission. Cancel this draft and raise a smaller
                          request, or ask {lower(t.financeTeam)} to move budget across and then submit it again.
                        </p>
                      )}
                    </Alert>
                  </div>
                ) : null}
              </Card>

              <Modal
                open={cancelling}
                onClose={() => setCancelling(false)}
                title={`Cancel ${createdNo ?? `this ${lower(t.requisition)}`}`}
                footer={
                  <>
                    <button type="button" className="btn" onClick={() => setCancelling(false)}>
                      Keep the draft
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={cancelReason.trim().length < 3 || cancelDraft.busy}
                      onClick={() => void onCancelDraft()}
                    >
                      {cancelDraft.busy ? 'Cancelling…' : 'Cancel the draft'}
                    </button>
                  </>
                }
              >
                <p>
                  The draft is withdrawn and any budget it reserved is released. The reason is kept with the record.
                </p>
                <TextArea
                  label="Reason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.currentTarget.value)}
                  required
                />
                <ErrorNotice error={cancelDraft.error} />
              </Modal>
            </div>
          );
        }}
      </AsyncPanel>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Budget check detail
 * ------------------------------------------------------------------ */

/**
 * The shape the server sends, exactly.
 *
 * Every figure except the request itself lives on the coding line's `position`
 * — the budget, what has been spent against it and what is already committed
 * are properties of the budget, not of this request — and the amount by which
 * the request breaks it is `breachMinor`.
 */
interface BudgetCheckLine {
  accountCode?: string;
  costCentreCode?: string;
  requestedMinor: number;
  position: {
    budgetMinor: number;
    actualMinor: number;
    commitmentMinor: number;
    availableMinor: number;
  };
  availableAfterMinor?: number;
  breachMinor: number;
  outcome: string;
  message?: string;
}

interface BudgetCheckResult {
  outcome: string;
  enforcement?: string;
  canOverride?: boolean;
  lines?: BudgetCheckLine[];
  message?: string;
  totalRequestedMinor?: number;
  totalBreachMinor?: number;
}

interface SubmitResult {
  requisitionNo: string;
  status: string;
  budgetCheck: BudgetCheckResult | null;
  approval: { firstApproverName: string | null; stageCount: number } | null;
}

/**
 * A budget refusal carries the lines it refused inside the error itself, so the
 * panel can still be filled in if the follow-up read of the full position
 * fails. They are the same objects the budget-check endpoint returns.
 */
function blockedLinesFrom(error: ApiError): BudgetCheckResult | null {
  const lines = error.details.lines;
  if (!Array.isArray(lines)) return null;
  const totalBreach = error.details.totalBreachMinor;
  return {
    outcome: 'block',
    message: error.message,
    lines: lines as BudgetCheckLine[],
    totalBreachMinor: typeof totalBreach === 'number' ? totalBreach : undefined,
    canOverride: error.details.canOverride === true,
  };
}

function BudgetCheckDetail({ result }: { result: BudgetCheckResult }) {
  if (!result.lines || result.lines.length === 0) {
    return result.message ? <p className="smaller">{result.message}</p> : null;
  }
  const totalRequested = result.lines.reduce((sum, l) => sum + Number(l.requestedMinor), 0);
  const totalBreach = result.lines.reduce((sum, l) => sum + Number(l.breachMinor), 0);
  return (
    <div className="table-wrap" style={{ marginTop: '0.75rem' }}>
      <table className="data">
        <caption className="visually-hidden">Budget position for each coding combination on this request</caption>
        <thead>
          <tr>
            <th scope="col">Coding</th>
            <th scope="col" className="num">
              Budget
            </th>
            <th scope="col" className="num">
              Spent
            </th>
            <th scope="col" className="num">
              Committed
            </th>
            <th scope="col" className="num">
              Available
            </th>
            <th scope="col" className="num">
              Requested
            </th>
            <th scope="col" className="num">
              Short by
            </th>
          </tr>
        </thead>
        <tbody>
          {result.lines.map((l, i) => (
            <tr key={i}>
              <td className="mono smaller">
                {l.accountCode ?? '—'} / {l.costCentreCode ?? '—'}
              </td>
              <td className="num">{money(l.position.budgetMinor)}</td>
              <td className="num">{money(l.position.actualMinor)}</td>
              <td className="num">{money(l.position.commitmentMinor)}</td>
              <td className="num">{money(l.position.availableMinor)}</td>
              <td className="num">{money(l.requestedMinor)}</td>
              <td className="num num--negative">{l.breachMinor > 0 ? money(l.breachMinor) : '—'}</td>
            </tr>
          ))}
        </tbody>
        {result.lines.length > 1 ? (
          <tfoot>
            <tr className="is-total">
              <td colSpan={5}>Total</td>
              <td className="num">{money(totalRequested)}</td>
              <td className="num num--negative">{totalBreach > 0 ? money(totalBreach) : '—'}</td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Requisition detail
 * ------------------------------------------------------------------ */

interface RequisitionDetail {
  requisition: Record<string, unknown> & {
    id: string;
    requisition_no: string;
    title: string;
    status: string;
    justification: string | null;
    gross_total_minor: string | number;
    net_total_minor: string | number;
    vat_total_minor: string | number;
    supplier_id: string | null;
    supplier_name: string | null;
    proposed_supplier_name: string | null;
    requested_by_name: string | null;
    cost_centre_code: string | null;
    cost_centre_name: string | null;
    created_at: string;
    submitted_at: string | null;
    date_required: string | null;
    budget_check_outcome: string | null;
    budget_override_reason: string | null;
    budget_overridden_by_name: string | null;
  };
  lines: Array<Record<string, unknown> & { id: string; line_no: number; description: string; quantity: string; unit_price_minor: string | number; line_net_minor: string | number; account_code: string; account_name: string; cost_centre_code: string; project_code: string | null }>;
  approval: {
    request: Record<string, unknown> | null;
    /*
     * Named as the workflow engine names them: `assigned_to_name` is whose desk
     * it is on, `acted_by_name` is who decided, and the authority the decision
     * was taken under is stamped on the step at the moment of the decision, so
     * a later change to somebody's limit cannot rewrite history.
     */
    steps: Array<Record<string, unknown> & {
      id: string;
      stage_no: number;
      stage_name: string;
      status: string;
      assigned_to_name: string | null;
      acted_by_name: string | null;
      on_behalf_of_name: string | null;
      acted_at: string | null;
      due_at: string | null;
      decision_comment: string | null;
      approval_limit_at_decision_minor: string | number | null;
    }>;
    events: Array<Record<string, unknown> & { id: string; event_type: string; description: string; occurred_at: string; actor_label: string | null }>;
  } | null;
  budgetChecks: Array<Record<string, unknown> & { id: string; outcome: string; checked_at: string; account_code: string | null; cost_centre_code: string | null; budget_minor: string | number; available_minor: string | number; requested_minor: string | number; message: string | null; checked_by_name: string | null }>;
  order: { id: string; order_no: string; status: string; issued_at: string | null } | null;
}

export function RequisitionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const t = useTerms();
  const { can } = useSession();
  const navigate = useNavigate();
  const detail = useAsync(() => api.get<RequisitionDetail>(`/procurement/requisitions/${id}`), [id]);
  const [creatingOrder, setCreatingOrder] = useState(false);

  const createOrder = useMutation(async () =>
    api.post<{ id: string; orderNo: string }>('/procurement/orders/from-requisition', { requisitionId: id }),
  );

  return (
    <AsyncPanel state={detail}>
      {(data) => {
        const r = data.requisition;
        const supplierName = r.supplier_name ?? r.proposed_supplier_name;
        return (
          <>
            <PageHeader
              title={r.title}
              subtitle={
                <>
                  <span className="mono">{r.requisition_no}</span> · raised by {r.requested_by_name ?? '—'} ·{' '}
                  {date(r.created_at)}
                </>
              }
              meta={
                <>
                  <Badge tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
                  {r.budget_check_outcome ? (
                    <Badge tone={budgetTone(r.budget_check_outcome)}>
                      Budget check: {budgetLabel(r.budget_check_outcome)}
                    </Badge>
                  ) : null}
                  {data.order ? (
                    <Link to={`/orders/${data.order.id}`}>
                      <span className="mono">{data.order.order_no}</span>
                    </Link>
                  ) : null}
                </>
              }
              actions={
                r.status === 'approved' && !data.order && can('po.issue') ? (
                  <button type="button" className="btn btn--primary" onClick={() => setCreatingOrder(true)}>
                    Create the {lower(t.purchaseOrder)}
                  </button>
                ) : null
              }
            />

            <div className="grid grid--3">
              <div style={{ gridColumn: 'span 2' }} className="stack">
                <Card title="Lines" flush>
                  <DataTable
                    caption={`Lines on ${r.requisition_no}`}
                    rows={data.lines}
                    rowKey={(l) => l.id}
                    emptyMessage="This request has no lines."
                    columns={[
                      { key: 'no', header: '#', width: '3rem', render: (l) => l.line_no },
                      { key: 'desc', header: 'Description', render: (l) => l.description },
                      {
                        key: 'coding',
                        header: 'Coding',
                        render: (l) => (
                          <span className="mono smaller">
                            {l.account_code} / {l.cost_centre_code}
                            {l.project_code ? ` / ${l.project_code}` : ''}
                          </span>
                        ),
                      },
                      { key: 'qty', header: 'Qty', numeric: true, render: (l) => number(l.quantity) },
                      {
                        key: 'unit',
                        header: 'Unit price',
                        numeric: true,
                        render: (l) => <Money minor={Number(l.unit_price_minor)} />,
                      },
                      {
                        key: 'net',
                        header: 'Net',
                        numeric: true,
                        render: (l) => <Money minor={Number(l.line_net_minor)} />,
                      },
                    ]}
                    footer={
                      <>
                        <tr className="is-subtotal">
                          <td colSpan={5}>Net</td>
                          <td className="num">{money(Number(r.net_total_minor))}</td>
                        </tr>
                        <tr className="is-subtotal">
                          <td colSpan={5}>VAT</td>
                          <td className="num">{money(Number(r.vat_total_minor))}</td>
                        </tr>
                        <tr className="is-total">
                          <td colSpan={5}>Total</td>
                          <td className="num">{money(Number(r.gross_total_minor))}</td>
                        </tr>
                      </>
                    }
                  />
                </Card>

                {data.budgetChecks.length > 0 ? (
                  <Card
                    title="Budget checks"
                    subtitle="Every check is logged, including the ones that passed. This is the evidence that control operated."
                    flush
                  >
                    <DataTable
                      caption="Budget checks run against this requisition"
                      rows={data.budgetChecks}
                      rowKey={(b) => b.id}
                      columns={[
                        {
                          key: 'when',
                          header: 'When',
                          render: (b) => (
                            <>
                              <div className="smaller">{dateTime(b.checked_at)}</div>
                              {b.checked_by_name ? <div className="smaller muted">{b.checked_by_name}</div> : null}
                            </>
                          ),
                        },
                        {
                          key: 'coding',
                          header: 'Coding',
                          render: (b) => (
                            <>
                              <span className="mono smaller">
                                {b.account_code ?? '—'} / {b.cost_centre_code ?? '—'}
                              </span>
                              {/* The check's own words, not our summary of them. */}
                              {b.message ? <div className="smaller muted">{b.message}</div> : null}
                            </>
                          ),
                        },
                        { key: 'budget', header: 'Budget', numeric: true, render: (b) => <Money minor={Number(b.budget_minor)} /> },
                        {
                          key: 'available',
                          header: 'Available',
                          numeric: true,
                          render: (b) => <Money minor={Number(b.available_minor)} />,
                        },
                        {
                          key: 'requested',
                          header: 'Requested',
                          numeric: true,
                          render: (b) => <Money minor={Number(b.requested_minor)} />,
                        },
                        {
                          key: 'outcome',
                          header: 'Outcome',
                          render: (b) => <Badge tone={budgetTone(b.outcome)}>{budgetLabel(b.outcome)}</Badge>,
                        },
                      ]}
                    />
                  </Card>
                ) : null}

                {r.budget_override_reason ? (
                  <Alert tone="warn" title="Budget override recorded">
                    <p>{r.budget_override_reason}</p>
                    {r.budget_overridden_by_name ? (
                      <p className="smaller muted">Overridden by {r.budget_overridden_by_name}.</p>
                    ) : null}
                  </Alert>
                ) : null}
              </div>

              <div className="stack">
                <Card title="Details">
                  <DefinitionList
                    items={[
                      [
                        t.supplier,
                        supplierName ? (
                          r.supplier_id && can('supplier.view') ? (
                            <Link to={`/suppliers/${r.supplier_id}`}>{supplierName}</Link>
                          ) : (
                            <>
                              {supplierName}
                              {!r.supplier_id ? <span className="smaller muted"> · proposed, not yet on file</span> : null}
                            </>
                          )
                        ) : (
                          'Not yet chosen'
                        ),
                      ],
                      [t.costCentre, r.cost_centre_code ? `${r.cost_centre_code} — ${r.cost_centre_name}` : '—'],
                      ['Date required', r.date_required ? date(r.date_required) : '—'],
                      ['Submitted', r.submitted_at ? dateTime(r.submitted_at) : 'Not yet submitted'],
                      ['Value', money(Number(r.gross_total_minor))],
                    ]}
                  />
                  {r.justification ? (
                    <>
                      <p className="field__label" style={{ marginTop: '1rem' }}>
                        Why it is needed
                      </p>
                      <p className="muted">{r.justification}</p>
                    </>
                  ) : null}
                </Card>

                <Card title="Approval" subtitle="Who decided, when, and on what authority.">
                  {!data.approval || data.approval.steps.length === 0 ? (
                    <EmptyState title="Not yet submitted for approval" />
                  ) : (
                    <ul className="timeline">
                      {data.approval.steps.map((step) => {
                        const decided = step.acted_by_name !== null;
                        const who = step.acted_by_name ?? step.assigned_to_name;
                        return (
                          <li
                            key={step.id}
                            className={
                              step.status === 'approved'
                                ? 'timeline__item timeline__item--ok'
                                : step.status === 'rejected'
                                  ? 'timeline__item timeline__item--error'
                                  : 'timeline__item'
                            }
                          >
                            <p className="timeline__title">
                              {step.stage_no}. {step.stage_name} <Badge tone={statusTone(step.status)}>{statusLabel(step.status)}</Badge>
                            </p>
                            {who ? (
                              <p className="timeline__detail">
                                {decided ? who : `With ${who}`}
                                {step.on_behalf_of_name ? (
                                  <span className="muted"> · on behalf of {step.on_behalf_of_name}</span>
                                ) : null}
                                {step.approval_limit_at_decision_minor !== null &&
                                step.approval_limit_at_decision_minor !== undefined ? (
                                  <span className="muted">
                                    {' '}
                                    · authority {money(Number(step.approval_limit_at_decision_minor))}
                                  </span>
                                ) : null}
                              </p>
                            ) : null}
                            {step.decision_comment ? <p className="timeline__detail">“{step.decision_comment}”</p> : null}
                            {step.acted_at ? (
                              <p className="timeline__time">{dateTime(step.acted_at)}</p>
                            ) : step.due_at ? (
                              <p className="timeline__time">Due {date(step.due_at)}</p>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Card>
              </div>
            </div>

            <Modal
              open={creatingOrder}
              onClose={() => setCreatingOrder(false)}
              title={`Create the ${lower(t.purchaseOrder)}`}
              footer={
                <>
                  <button type="button" className="btn" onClick={() => setCreatingOrder(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={createOrder.busy}
                    onClick={() =>
                      void createOrder.run().then((result) => {
                        if (result) navigate(`/orders/${result.id}`);
                      })
                    }
                  >
                    {createOrder.busy ? 'Creating…' : `Create ${lower(t.purchaseOrder)}`}
                  </button>
                </>
              }
            >
              <p>
                This converts the approved {lower(t.requisition)} into a {lower(t.purchaseOrder)}. The soft commitment
                held against the budget becomes a hard commitment when the order is issued to the {lower(t.supplier)}.
              </p>
              <ErrorNotice error={createOrder.error} />
            </Modal>
          </>
        );
      }}
    </AsyncPanel>
  );
}
